import { useCallback, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import type { RootState, AppDispatch } from '@center-pwa/store/store';
import type { Schema } from 'types/schema';
import type { SchemaTypeStatus, SchemaTypeEntry } from '@center-pwa/reducers/schemaTypesReducer';
import { register } from './requestCoalescer';

export interface SchemaTypeResult {
  schema: Schema | null;
  status: SchemaTypeStatus;
}

export interface SingleSchemaTypeResult extends SchemaTypeResult {
  /** Re-request a `failed` type. A no-op in every other status. */
  retry: () => void;
}

/**
 * Resolve a single schema type by system_name on demand.
 *
 * On first call with an idle/unknown key the hook registers the name with the
 * request coalescer (which dispatches setLoading immediately and issues a
 * batched GET /schemas after COALESCE_WINDOW_MS). Returns the current slice
 * entry on every render so consumers re-render when the status changes.
 */
export function useSchemaType(systemName: string | null | undefined): SingleSchemaTypeResult {
  const dispatch = useDispatch<AppDispatch>();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const key = organizationId && systemName ? `${organizationId}::${systemName}` : null;

  const entry = useSelector((state: RootState) =>
    key ? state.schemaTypes.byKey[key] : undefined,
  );

  // Register with the coalescer if this key hasn't been fetched yet.
  // entry?.status is read from the render that triggered this effect so the
  // coalescer can skip keys already in-flight or resolved. Intentionally
  // excludes entry?.status and apiClients?.schemaClient: we want the effect
  // to fire once per key change, not on every status transition.
  useEffect(() => {
    if (!key || !organizationId || !apiClients?.schemaClient || !systemName) return;
    register(systemName, organizationId, apiClients.schemaClient, dispatch, entry?.status ?? null);
  }, [key, organizationId, systemName, dispatch]);

  const status = entry?.status ?? 'idle';
  const retry = useCallback(() => {
    if (status !== 'failed' || !organizationId || !apiClients?.schemaClient || !systemName) return;
    register(systemName, organizationId, apiClients.schemaClient, dispatch, status);
  }, [status, organizationId, apiClients?.schemaClient, systemName, dispatch]);

  return {
    schema: entry?.data ?? null,
    status,
    retry,
  };
}

/**
 * Resolve multiple schema types by system_name in a single coalesced request.
 *
 * Keys that are already loading/succeeded/notFound in the Redux store are
 * skipped (dedup). Returns a name → result map.
 */
export function useSchemaTypes(
  systemNames: string[],
): Record<string, SchemaTypeResult> {
  const dispatch = useDispatch<AppDispatch>();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  // Stable string representation to use as effect dependency
  const namesKey = useMemo(
    () => [...systemNames].sort((a, b) => a.localeCompare(b)).join(','),
    [systemNames],
  );

  const entries = useSelector((state: RootState): Record<string, SchemaTypeEntry | undefined> => {
    if (!organizationId) return {};
    const result: Record<string, SchemaTypeEntry | undefined> = {};
    for (const name of systemNames) {
      result[name] = state.schemaTypes.byKey[`${organizationId}::${name}`];
    }
    return result;
  });

  // entries and systemNames intentionally excluded: we read status from the
  // render that triggered this effect (same rationale as useSchemaType above);
  // namesKey is the stable scalar dependency for systemNames.
  useEffect(() => {
    if (!organizationId || !apiClients?.schemaClient) return;

    for (const name of systemNames) {
      const currentStatus = entries[name]?.status ?? null;
      register(name, organizationId, apiClients.schemaClient, dispatch, currentStatus);
    }
  }, [organizationId, apiClients?.schemaClient, namesKey, dispatch]);

  // systemNames intentionally excluded: namesKey is its stable scalar form.
  return useMemo(() => {
    const result: Record<string, SchemaTypeResult> = {};
    for (const name of systemNames) {
      const e = entries[name];
      result[name] = { schema: e?.data ?? null, status: e?.status ?? 'idle' };
    }
    return result;
  }, [entries, namesKey]);
}

